/**
 * Date format — the DECLARED COLUMN FACT rail for date-typed columns (declared-fact
 * instance #2; see declared-column-facts.ts for the framework and ./date-parse.ts
 * for the grammar + detector).
 *
 * A two-number slash/dash date column (`04/07/2025`) cannot always be READ from its
 * own values: when every day is ≤ 12, Month/Day and Day/Month produce different —
 * both plausible — calendar dates. Booking July 4th as April 7th shifts every
 * observation month, so the orientation is a declared, durable, per-column fact:
 * detected from the data where the data can prove it, surfaced as a correctable
 * chip on the mapping row, persisted on the org's field registry with provenance,
 * and applied as the authoritative parse at ingest.
 *
 * AMBIGUITY POLICY (founder-ruled 2026-07-18, SUPERSEDES the earlier must-ask
 * design): customers are US-only, so an ambiguous column PREFILLS the US Month/Day
 * default (`mdy`) with provenance `suggested` — visible, correctable, NEVER
 * blocking Continue/finalize. Data evidence still wins over the default (a
 * position-1 value > 12 proves `dmy` and suggests it); a cell that does not fit
 * the effective format reads as NO value (`wrong_format`) — never guessed.
 *
 * This module is the pure half: provenance vocabulary, the US default, the fact
 * key resolution, and the ONE stickiness/contradiction resolver
 * ({@link resolveDateFormatState} — the exact sibling of
 * `resolveReportingBasisState`). The storage + detection SQL live in
 * server/db/uploads/date-format.ts.
 */

import type { DateFormatDetection, DeclaredDateFormat } from './declared-column-facts';
import { getStandardField, isStandardFieldKey } from './catalog';
import type { StandardFieldKey } from './catalog';
import { CUSTOM_FIELD_KEY_PREFIX, customCanonicalKey } from './portfolio-model';

/**
 * Where a stored date format came from. `suggested` = the detection prefill (or
 * the US default for an ambiguous column) saved without editing — re-suggested
 * when new data contradicts it; `user_declared` = the customer explicitly chose
 * it (sticky — never overwritten by a suggestion or the default).
 */
export const DATE_FORMAT_PROVENANCES = ['suggested', 'user_declared'] as const;
export type DateFormatProvenance = (typeof DATE_FORMAT_PROVENANCES)[number];

/**
 * The US Month/Day default an AMBIGUOUS two-number column prefills (founder-ruled
 * 2026-07-18: US-only customer base). Provenance is always `suggested` — the
 * default is a visible, correctable assumption, never a silent guess.
 */
export const US_DEFAULT_DATE_FORMAT: DeclaredDateFormat = 'mdy';

/** One stored date-format declaration (format + provenance). */
export interface StoredDateFormat {
  readonly format: DeclaredDateFormat;
  readonly provenance: DateFormatProvenance;
}

/**
 * Where the chip's effective prefill came from — drives the UI affordance:
 *  - `user`    — the customer's sticky declaration (no suggestion tag);
 *  - `data`    — detection PROVED it from the column's own values
 *                ("matches your data");
 *  - `stored`  — a surviving stored suggestion the fresh detection neither
 *                proves nor contradicts (this file's data is silent);
 *  - `default` — the US Month/Day assumption for an ambiguous column (labeled
 *                as a suggestion, correctable — never blocking).
 */
export type DateFormatSource = 'user' | 'data' | 'stored' | 'default';

/** The resolved chip state: the EFFECTIVE format is never null — dates never block. */
export interface ResolvedDateFormatState {
  /** The effective prefill (and the format finalize persists when unedited). */
  readonly format: DeclaredDateFormat;
  /** Provenance the persist carries (`user_declared` iff sticky-stored). */
  readonly provenance: DateFormatProvenance;
  /** Where the prefill came from (see {@link DateFormatSource}). */
  readonly source: DateFormatSource;
}

/**
 * Resolve the chip's effective prefill from the stored declaration and a fresh
 * detection (the ONE place the stickiness/contradiction rules live — the
 * detection endpoint, the finalize persist, and the analyze payload all read
 * this; the exact sibling of `resolveReportingBasisState`):
 *
 *  1. a `user_declared` format is STICKY: prefill it, ignore detection;
 *  2. data evidence wins: an unambiguous detection suggestion is the prefill —
 *     which also RE-SUGGESTS over a contradicting stored suggestion (e.g. a
 *     stored `mdy` meets a file whose position-1 values exceed 12 → `dmy`);
 *  3. no data evidence (ambiguous, conflicted, or nothing recognized): a stored
 *     suggestion HOLDS (silence does not contradict it — this includes a
 *     conflict column, where neither orientation is proven one-sided);
 *  4. nothing stored either → the US Month/Day default (`mdy`, provenance
 *     `suggested`) per the 2026-07-18 founder ruling. NEVER null, NEVER
 *     blocking — every date-mapped column resolves a format.
 */
export function resolveDateFormatState(
  stored: StoredDateFormat | null,
  detection: DateFormatDetection,
): ResolvedDateFormatState {
  if (stored !== null && stored.provenance === 'user_declared') {
    return { format: stored.format, provenance: 'user_declared', source: 'user' };
  }
  if (detection.suggested !== null) {
    return { format: detection.suggested, provenance: 'suggested', source: 'data' };
  }
  if (stored !== null) {
    return { format: stored.format, provenance: 'suggested', source: 'stored' };
  }
  return { format: US_DEFAULT_DATE_FORMAT, provenance: 'suggested', source: 'default' };
}

/**
 * The org-registry key a column's date-format fact is stored under
 * (`portfolio_fields.key` — RB-6 storage shape, standard AND custom rows):
 *  - a DATE-typed Standard target → the standard field key itself;
 *  - a Custom target whose effective value type is `date` → the namespaced
 *    canonical custom key (`customCanonicalKey(sourceColumn)`);
 *  - anything else → null (the column carries no date-format fact).
 *
 * `customValueType` is the custom column's EFFECTIVE durable type (registry type
 * for an existing field, else the chosen/inferred type); ignored for Standard
 * targets (catalog type wins).
 */
export function dateFormatFactKey(
  target: StandardFieldKey | 'custom',
  sourceColumn: string,
  customValueType?: string | null,
): string | null {
  if (target !== 'custom') {
    return getStandardField(target).valueType === 'date' ? target : null;
  }
  return customValueType === 'date' ? customCanonicalKey(sourceColumn) : null;
}

/**
 * Whether a raw registry key can carry a date-format fact: a DATE-typed Standard
 * field key, or a namespaced custom key (`c:` prefix — the custom row's own
 * valueType is checked at storage time). The detection endpoint's key guard.
 */
export function isDateFormatFactKey(key: string): boolean {
  if (isStandardFieldKey(key)) {
    return getStandardField(key).valueType === 'date';
  }
  return key.startsWith(CUSTOM_FIELD_KEY_PREFIX);
}
